import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BridgeBadge } from "../components/BridgeBadge";
import { TauriWindowControls } from "../components/TauriWindowControls";
import { ThemeToggle } from "../components/ThemeToggle";
import { useAuth } from "../auth/AuthContext";
import { useBridgePoll } from "../hooks/useBridgePoll";

interface NavItem {
  to: string;
  label: string;
  /** Inline SVG nav icon. Stroke icons (Lucide-style) at 16px, kept
   *  consistent across the sidebar — see the skill's icon-style-consistent
   *  rule. */
  Icon: () => JSX.Element;
}

function I(path: string): () => JSX.Element {
  // Tiny wrapper so each NavItem reads as one line. Uses currentColor so
  // the icon inherits text-fg / text-primary from the nav-link state.
  // eslint-disable-next-line react/display-name
  return () => (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );
}

const NAV: NavItem[] = [
  // Lucide path data, mapped to each section's metaphor.
  { to: "/live", label: "Live", Icon: I("M12 2v2 M12 20v2 M4.93 4.93l1.41 1.41 M17.66 17.66l1.41 1.41 M2 12h2 M20 12h2 M4.93 19.07l1.41-1.41 M17.66 6.34l1.41-1.41 M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z") },
  { to: "/overview", label: "Overview", Icon: I("M3 12h4l3-8 4 16 3-8h4") },
  { to: "/positions", label: "Positions", Icon: I("M3 3v18h18 M7 14l4-4 4 4 4-4") },
  { to: "/trades", label: "Trades", Icon: I("M3 6h18 M3 12h18 M3 18h18") },
  { to: "/journal", label: "Journal", Icon: I("M4 4h12a4 4 0 0 1 4 4v12 M4 4v16h12 M8 8h8 M8 12h6 M8 16h4") },
  { to: "/news", label: "News", Icon: I("M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11l5 5v9a2 2 0 0 1-2 2z M7 9h6 M7 13h10 M7 17h10") },
  { to: "/backtest", label: "Backtest", Icon: I("M5 3v18 M5 21l16-9L5 3z") },
  { to: "/intelligence", label: "Intelligence", Icon: I("M12 2a7 7 0 0 0-4 12.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26A7 7 0 0 0 12 2z M10 22h4") },
  { to: "/trading-account", label: "Trading account", Icon: I("M3 7h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z M3 7l3-3h12l3 3 M9 12h6 M9 16h4") },
  { to: "/trading-config", label: "Trading config", Icon: I("M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M1 14h6 M9 8h6 M17 16h6") },
  { to: "/credentials", label: "Credentials", Icon: I("M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4") },
  { to: "/tradingview", label: "TradingView", Icon: I("M3 17l6-6 4 4 8-8 M14 7h7v7") },
  { to: "/manual", label: "Manual", Icon: I("M4 4h11a4 4 0 0 1 4 4v12 M4 4v16h11a3 3 0 0 1 3 3 M8 8h7 M8 12h7 M8 16h5") },
  { to: "/settings", label: "Settings", Icon: I("M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z") },
];

const SIDEBAR_COLLAPSED_KEY = "fxdash.sidebar.collapsed";

function LogoutIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9" />
    </svg>
  );
}

export function AppLayout() {
  const { status, logout } = useAuth();
  const bridge = useBridgePoll();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  }

  const user = status?.current_user;

  return (
    <div className="flex min-h-screen bg-page text-fg">
      <aside
        className={`flex flex-col border-r border-border bg-surface-2 transition-[width] duration-150 ${
          collapsed ? "w-16" : "w-56"
        }`}
      >
        {/* Brand + collapse */}
        <div
          className={`flex h-14 items-center border-b border-border px-3 ${
            collapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!collapsed && (
            <span className="font-mono text-base font-bold tracking-tight text-fg">
              FX-Dash
            </span>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="rounded-md p-1 text-fg-muted transition hover:bg-bg-hover hover:text-fg"
          >
            <span className="font-mono text-sm">{collapsed ? "›" : "‹"}</span>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3">
          <ul className="space-y-0.5 px-2">
            {NAV.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-fg-muted hover:bg-bg-hover hover:text-fg"
                    } ${collapsed ? "justify-center" : ""}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Left accent bar = active marker. Quiet enough to
                          not shout, distinct enough to scan at a glance. */}
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary"
                        />
                      )}
                      <Icon />
                      {!collapsed && <span>{label}</span>}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer: user · theme · logout */}
        <div className="border-t border-border p-2">
          {!collapsed && user && (
            <p
              className="mb-1 truncate px-2 text-xs text-fg-muted"
              title={user.email}
            >
              {user.email}
            </p>
          )}
          <ThemeToggle compact={collapsed} />
          <button
            type="button"
            onClick={logout}
            title="Log out"
            className={`mt-1 flex items-center gap-2 rounded-md px-2 py-2 text-xs uppercase tracking-wide text-fg-muted transition hover:bg-bg-hover hover:text-danger ${
              collapsed ? "w-auto justify-center" : "w-full"
            }`}
          >
            <LogoutIcon />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-4">
          <div className="flex items-center gap-3 text-xs">
            <TauriWindowControls />
          </div>
          <div className="flex items-center gap-3 text-xs text-fg-muted">
            <BridgeBadge bridge={bridge} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-page p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
